import type { Edge, Node } from "@xyflow/react";
import {
	createGenerationNode,
	GENERATION_NODE_WIDTH,
	GENERATION_ROW_HEIGHT,
	generationCardsFor,
	generationSlotPosition,
	isGenerationNode,
} from "./board-graph";

/** The shared part of a board: what every open window must agree on. */
export interface CanvasState {
	edges: Edge[];
	nodes: Node[];
}

/** Strip React Flow runtime fields so the stored document stays small and stable. */
export function serializeNodes(nodes: Node[]): Node[] {
	return nodes.map(({ id, type, position, data, width, height }) => ({
		id,
		type,
		position,
		data,
		...(width !== undefined && height !== undefined ? { width, height } : {}),
	}));
}

export function serializeEdges(edges: Edge[]): Edge[] {
	return edges.map(({ id, source, target, sourceHandle, targetHandle }) => ({
		id,
		source,
		target,
		...(sourceHandle ? { sourceHandle } : {}),
		...(targetHandle ? { targetHandle } : {}),
	}));
}

export function serializeCanvas(state: CanvasState): string {
	return JSON.stringify({
		nodes: serializeNodes(state.nodes),
		edges: serializeEdges(state.edges),
	});
}

function mergeById<T extends { id: string }>(
	base: T[],
	local: T[],
	remote: T[],
	key: (item: T) => string
): T[] {
	const baseById = new Map(base.map((item) => [item.id, key(item)]));
	const localById = new Map(local.map((item) => [item.id, item]));
	const remoteIds = new Set(remote.map((item) => item.id));
	const merged: T[] = [];

	for (const item of remote) {
		const mine = localById.get(item.id);
		const wasInBase = baseById.has(item.id);
		if (!mine) {
			// Deleted here (it was in the base), or added remotely.
			if (!wasInBase) {
				merged.push(item);
			}
			continue;
		}
		const changedHere = !wasInBase || key(mine) !== baseById.get(item.id);
		merged.push(changedHere ? mine : item);
	}
	for (const item of local) {
		// Added here and not yet saved; items deleted remotely stay deleted.
		if (!(remoteIds.has(item.id) || baseById.has(item.id))) {
			merged.push(item);
		}
	}
	return merged;
}

/**
 * Three-way merge of the canvas: `base` is the last state this window had in
 * sync with the server, `local` what it shows now, `remote` the newer server
 * state. Additions and deletions from both sides are kept; a node or edge
 * changed on both sides keeps this window's version.
 */
export function mergeCanvas(
	base: CanvasState,
	local: CanvasState,
	remote: CanvasState
): CanvasState {
	const nodeKey = (n: Node) => JSON.stringify(serializeNodes([n])[0]);
	const edgeKey = (e: Edge) => JSON.stringify(serializeEdges([e])[0]);
	return {
		nodes: mergeById(base.nodes, local.nodes, remote.nodes, nodeKey),
		edges: mergeById(base.edges, local.edges, remote.edges, edgeKey),
	};
}

export interface BoardGeneration {
	/** When it was registered; recent ones belong to the window running them. */
	createdAt?: string | Date;
	id: string;
	/** The prompt node that produced it. */
	nodeId: string | null;
}

const RECOVERY_GAP = 80;
/**
 * Only restore cards for generations at least this old. The window running a
 * generation adds its card itself; until that card is saved and merged here,
 * a younger generation would look "lost" and get a second card.
 */
export const RECOVERY_GRACE_MS = 60_000;

/** Duplicate cards for the same generation, keeping the lowest node id. */
function duplicateGenerationCardIds(nodes: Node[]): string[] {
	const cardsByGeneration = new Map<string, Node[]>();
	for (const node of nodes) {
		if (isGenerationNode(node)) {
			const list = cardsByGeneration.get(node.data.generationId) ?? [];
			list.push(node);
			cardsByGeneration.set(node.data.generationId, list);
		}
	}
	const removeIds: string[] = [];
	for (const cards of cardsByGeneration.values()) {
		const sorted = [...cards].sort((a, b) => a.id.localeCompare(b.id));
		removeIds.push(...sorted.slice(1).map((n) => n.id));
	}
	return removeIds;
}

/**
 * Drop duplicate generation cards (and their edges). Every window keeps the
 * same card — the lowest id, i.e. the oldest — so they converge.
 */
export function dedupeGenerationCards(state: CanvasState): CanvasState {
	const removed = new Set(duplicateGenerationCardIds(state.nodes));
	if (removed.size === 0) {
		return state;
	}
	return {
		nodes: state.nodes.filter((n) => !removed.has(n.id)),
		edges: state.edges.filter(
			(e) => !(removed.has(e.source) || removed.has(e.target))
		),
	};
}

/**
 * Cards for generations that belong to this board but have no card (e.g. lost
 * to an older window overwriting the board), placed next to their prompt when
 * it's still there. Also returns duplicate cards to drop, keeping the one with
 * the lowest id so every window drops the same card.
 */
export function reconcileGenerationCards(
	nodes: Node[],
	edges: Edge[],
	generations: BoardGeneration[],
	now: number = Date.now()
): { add: Node[]; removeIds: string[] } {
	const removeIds = duplicateGenerationCardIds(nodes);
	const withCard = new Set(
		nodes.filter(isGenerationNode).map((n) => n.data.generationId)
	);

	const byId = new Map(nodes.map((n) => [n.id, n]));
	const rightEdge = nodes.reduce(
		(max, n) =>
			Math.max(max, n.position.x + (n.width ?? GENERATION_NODE_WIDTH)),
		0
	);
	const placedPerPrompt = new Map<string, number>();
	let looseIndex = 0;
	const add: Node[] = [];
	for (const gen of generations) {
		if (withCard.has(gen.id)) {
			continue;
		}
		const createdAt = gen.createdAt ? new Date(gen.createdAt).getTime() : 0;
		if (now - createdAt < RECOVERY_GRACE_MS) {
			continue;
		}
		const prompt = gen.nodeId ? byId.get(gen.nodeId) : undefined;
		if (prompt) {
			const slot =
				placedPerPrompt.get(prompt.id) ??
				generationCardsFor(nodes, edges, prompt.id);
			placedPerPrompt.set(prompt.id, slot + 1);
			add.push(
				createGenerationNode(gen.id, generationSlotPosition(prompt, slot))
			);
		} else {
			add.push(
				createGenerationNode(gen.id, {
					x: rightEdge + RECOVERY_GAP,
					y: looseIndex * GENERATION_ROW_HEIGHT,
				})
			);
			looseIndex += 1;
		}
	}
	return { add, removeIds };
}
